/**
 * TypeScript interfaces for Krybot API
 * Matching Pydantic schemas from backend/app/schemas/krybot/
 */

import type { ThreadContext } from './threadContext';

/** Matches `krybot_agents.agent_type` (sql/120_expand_krybot_agent_types.sql). */
export const KRYBOT_AGENT_TYPES = [
  'chat',
  'prime',
  'company',
  'orchestrator',
  'session',
  'domain',
  'utility',
  'delivery',
] as const;

export type AgentType = (typeof KRYBOT_AGENT_TYPES)[number];

/** UI labels for agent type selects and filters. */
export const KRYBOT_AGENT_TYPE_LABELS: Record<AgentType, string> = {
  chat: 'Chat',
  prime: 'Prime',
  company: 'Company',
  orchestrator: 'Orchestrator',
  session: 'Session',
  domain: 'Domain',
  utility: 'Utility',
  delivery: 'Delivery',
};

/** Ant Design Tag colors for agent types (unknown → default). */
export function krybotAgentTypeTagColor(type: string | undefined): string {
  const colors: Record<string, string> = {
    chat: 'blue',
    prime: 'purple',
    company: 'orange',
    orchestrator: 'geekblue',
    session: 'cyan',
    domain: 'green',
    utility: 'gold',
    delivery: 'magenta',
  };
  return colors[type || ''] || 'default';
}
export type RunStatus = 'queued' | 'in_progress' | 'completed' | 'requires_approval' | 'failed' | 'cancelled';
export type ApprovalStatus = 'pending' | 'approved' | 'rejected' | 'expired';
export type ApprovalDecision = 'approve' | 'reject' | 'request_changes';
export type ActionType = 'read' | 'write' | 'chat' | 'validation' | 'approval_request';

/** ISSUE-193 — optional hint from LLM JSON; copied onto run `model_snapshot` by dispatcher */
export type KrybotApprovalTier = 'proposal_only' | 'persistence' | 'unknown';

export interface KrybotAgent {
  id: string;
  name: string;
  display_name?: string;
  description?: string;
  agent_type?: AgentType;
  persona_prompt?: string;
  capabilities?: Record<string, unknown>;
  default_context?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  is_active: boolean;
  parent_agent_id?: string;
  template_id?: string;
  template?: KrybotTemplate;
  created_at: string;
  updated_at: string;
  versions?: KrybotAgentVersion[];
}

export interface KrybotAgentVersion {
  id: string;
  agent_id: string;
  version_tag: string;
  change_summary?: string;
  persona_prompt?: string;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface KrybotAgentSummary {
  id: string;
  name: string;
  display_name?: string;
  agent_type?: AgentType;
  is_active: boolean;
  capabilities?: Record<string, unknown>;
  template?: KrybotTemplate;
}

export interface KrybotAgentCreate {
  name: string;
  display_name?: string;
  description?: string;
  agent_type?: AgentType;
  persona_prompt?: string;
  capabilities?: Record<string, unknown>;
  default_context?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  is_active?: boolean;
  parent_agent_id?: string;
  template_id?: string;
}

export interface KrybotAgentUpdate {
  display_name?: string;
  description?: string;
  agent_type?: AgentType;
  persona_prompt?: string;
  capabilities?: Record<string, unknown>;
  default_context?: Record<string, unknown>;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  is_active?: boolean;
  parent_agent_id?: string;
  template_id?: string;
}

export interface KrybotAgentVersionCreate {
  version_tag: string;
  change_summary?: string;
  persona_prompt?: string;
  input_schema?: Record<string, unknown>;
  output_schema?: Record<string, unknown>;
  is_active?: boolean;
}

export interface KrybotAgentRun {
  id: string;
  agent_id?: string;
  agent?: KrybotAgentSummary;
  thread_id?: string;
  user_id?: string;
  status: RunStatus;
  artifact_scope?: 'prime' | 'company' | 'none';
  prime_artifact_id?: string;
  company_artifact_id?: string;
  company_tree_id?: string;
  input_payload?: Record<string, unknown>;
  output_payload?: Record<string, unknown>;
  /** May include `approval_tier`, `system_prompt`, `response_id`, etc. */
  model_snapshot?: Record<string, unknown> & { approval_tier?: KrybotApprovalTier };
  error_detail?: Record<string, unknown>;
  parent_run_id?: string;
  /** Set when run participates in `krybot_orchestration` (DAG / spawn). */
  orchestration_id?: string | null;
  started_at?: string;
  completed_at?: string;
  created_at: string;
  updated_at: string;
  actions?: KrybotAgentAction[];
  messages?: ChatMessageResponse[];
  approval?: KrybotApprovalSummary;
}

export interface KrybotAgentRunStatus {
  id: string;
  agent_id?: string;
  status: RunStatus;
  started_at?: string;
  completed_at?: string;
  last_updated_at: string;
  prime_artifact_id?: string;
  company_artifact_id?: string;
  thread_id?: string;
  parent_run_id?: string;
  orchestration_id?: string | null;
}

export interface KrybotAgentRunCreate {
  agent_id: string;
  thread_id?: string;
  user_id?: string;
  artifact_scope?: 'prime' | 'company' | 'none';
  prime_artifact_id?: string;
  company_artifact_id?: string;
  company_tree_id?: string;
  input_payload?: Record<string, unknown>;
  prompt_overrides?: Record<string, unknown>;
}

export interface KrybotAgentRunResubmitRequest {
  additional_instructions: string;
  include_original_instruction?: boolean;
}

export interface KrybotAgentAction {
  id: string;
  sequence_order?: number;
  action_type: ActionType;
  status: string;
  request_payload?: Record<string, unknown>;
  response_payload?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface KrybotApproval {
  id: string;
  status: ApprovalStatus;
  approval_type: string;
  agent_run_id?: string;
  requested_by?: string;
  reviewer_id?: string;
  thread_id?: string;
  artifact_references?: Record<string, unknown>;
  proposed_changes?: Record<string, unknown>;
  review_notes?: Record<string, unknown>;
  approved_at?: string;
  created_at: string;
  expires_at?: string;
  updated_at: string;
  decisions?: KrybotApprovalDecision[];
}

export interface KrybotApprovalSummary {
  id: string;
  agent_run_id: string;  // Required - links approval to the agent run
  status: ApprovalStatus;
  approval_type: string;
  requested_by?: string;
  reviewer_id?: string;
  created_at: string;
  expires_at?: string;
}

export interface KrybotApprovalDecision {
  id: string;
  approval_id: string;
  decision: ApprovalDecision;
  decider_id: string;
  notes?: string;
  payload_diff?: Record<string, unknown>;
  created_at: string;
}

/** Matches ArtifactReviewDialog — REQ-KRYBOT-001 / ISSUE-199 */
export interface KrybotSelectedElements {
  artifact?: boolean;
  properties?: boolean;
  states?: string[];
  transitions?: string[];
  processes?: string[];
}

export interface KrybotApprovalDecisionRequest {
  decision: ApprovalDecision;
  notes?: string;
  payload_diff?: Record<string, unknown>;
  focus_area?: 'properties' | 'states' | 'transitions' | 'processes';
  selected_elements?: KrybotSelectedElements;
}

/** ISSUE-194 — surfaced on approve when artifact mutation runs */
export interface ArtifactApplyResult {
  success: boolean;
  artifact_type?: 'prime' | 'company';
  detail?: Record<string, unknown>;
  error?: string;
  error_type?: string;
}

/** Response body for POST /krybot/approvals/{id}/decision (not only the new decision row) */
export interface KrybotApprovalDecisionResponse {
  id: string;
  agent_run_id: string;
  thread_id?: string | null;
  approval_type?: string;
  status: ApprovalStatus;
  requested_by?: string | null;
  reviewer_id?: string | null;
  artifact_references?: Record<string, unknown>;
  proposed_changes?: Record<string, unknown>;
  review_notes?: Record<string, unknown>;
  expires_at?: string | null;
  approved_at?: string | null;
  created_at: string;
  updated_at: string;
  decisions: KrybotApprovalDecision[];
  artifact_apply_result?: ArtifactApplyResult | null;
}

export interface KrybotApprovalAssignmentRequest {
  reviewer_id: string;
}

export interface KrybotApprovalListResponse {
  approvals: KrybotApprovalSummary[];
  total: number;
}

export interface KrybotApprovalHistoryResponse {
  approval: KrybotApprovalSummary;
  decisions: KrybotApprovalDecision[];
}

export interface KrybotTemplate {
  id: string;
  template_key: string;
  template_type: string;
  sector?: string;
  prompt_text: string;
  metadata?: Record<string, unknown>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  versions?: KrybotTemplateVersion[];
}

export interface KrybotTemplateVersion {
  id: string;
  template_id: string;
  version_tag: string;
  prompt_text: string;
  metadata?: Record<string, unknown>;
  change_summary?: string;
  created_at: string;
  updated_at: string;
}

export interface KrybotTemplateCreate {
  template_key: string;
  template_type: string;
  sector?: string;
  prompt_text: string;
  metadata?: Record<string, unknown>;
  is_active?: boolean;
}

export interface KrybotTemplateUpdate {
  template_key?: string;
  template_type?: string;
  sector?: string;
  prompt_text?: string;
  metadata?: Record<string, unknown>;
  is_active?: boolean;
}

export interface KrybotTemplateVersionCreate {
  version_tag: string;
  prompt_text: string;
  metadata?: Record<string, unknown>;
  change_summary?: string;
}

export interface KrybotTemplateRenderRequest {
  template_key: string;
  variables: Record<string, unknown>;
}

export interface KrybotTemplateRenderResponse {
  template_key: string;
  rendered_text: string;
  variables: Record<string, unknown>;
  version_tag?: string;
}

// Chat-related types
export interface ChatBootstrapRequest {
  user_id: string;
  thread_id?: string;
  artifact_context?: Record<string, unknown>;
  ui_role?: string;
  preferred_agent_id?: string;
  metadata?: Record<string, unknown>;
  /** ISSUE-243 — merged in `KrybotService.bootstrapThread` from `ui_role` if omitted */
  agent_mode?: string;
  /** ISSUE-243 ThreadContext (snake_case); `bootstrapThread` always adds `context_version` */
  context?: Partial<ThreadContext> & Record<string, unknown>;
}

export interface ChatBootstrapResponse {
  thread: ChatThreadResponse;
  initial_prompt?: string;
  active_agent?: KrybotAgentSummary;
  starter_messages: ChatMessageResponse[];
  active_context?: Record<string, unknown>;
  /** KBO hygiene: true when bootstrap reclaimed an empty default-named draft */
  thread_reused?: boolean;
}

export interface ChatTurnRequest {
  thread_id: string;
  user_id: string;
  message_text: string;
  message_metadata?: {
    focus_areas?: ('properties' | 'states' | 'transitions' | 'processes')[];
    [key: string]: unknown;
  };
  agent_id?: string;
  tool_invocation?: Record<string, unknown>;
}

export interface ChatTurnResponse {
  thread: ChatThreadResponse;
  user_message: ChatMessageResponse;
  assistant_message?: ChatMessageResponse;
  agent_run?: KrybotAgentRun;
  pending_approval?: KrybotApprovalSummary;
}

export interface ChatHistoryResponse {
  thread: ChatThreadResponse;
  messages: ChatMessageResponse[];
  last_synced_at: string;
}

export interface ChatAttachmentUploadResponse {
  attachment_id: string;
  thread_id: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  download_url: string;
  artifact_item_ref?: string | null;
  created_at?: string;
}

export interface ChatPanelRecipientPayload {
  kind: 'agent' | 'person' | string;
  id: string;
  label?: string;
}

export interface PersonDeliverRequest {
  user_id: string;
  message_text: string;
  message_metadata?: Record<string, unknown>;
  chat_panel_recipients: ChatPanelRecipientPayload[];
}

export interface PersonDeliverResponse {
  thread_id: string;
  user_message: ChatMessageResponse;
  assistant_message?: ChatMessageResponse | null;
  delivery_mode: 'person_deliver';
  person_recipient_ids?: string[];
  email_notify?: Array<{ user_id: string; ok: boolean; error?: string | null }>;
}

export interface RouteExtractionResponse {
  thread_id: string;
  attachment_id: string;
  extraction_status: string;
  artifact_item_ref?: string | null;
  system_message: ChatMessageResponse;
}

export interface AgentHandoffRequest {
  thread_id: string;
  agent_id: string;
  reason?: string;
  metadata?: Record<string, unknown>;
}

export interface ThreadContextRefreshResponse {
  thread_id: string;
  active_context: Record<string, unknown>;
  refreshed_at: string;
}

// Re-export chat types (these should exist in the chat types file)
export interface ChatThreadResponse {
  id: string;
  name?: string;
  user_id: string;
  artifact_context?: Record<string, unknown>;
  ui_role?: string;
  /** ISSUE-243 normalized ThreadContext */
  context?: Partial<ThreadContext> & Record<string, unknown>;
  active_agent_id?: string;
  active_context?: Record<string, unknown>;
  pending_approval_id?: string;
  created_at: string;
  updated_at: string;
  /** KBO hygiene markers from GET /krybot/chat/threads */
  is_empty?: boolean;
  is_default_named?: boolean;
  message_count?: number;
  unread_count?: number;
  is_owner?: boolean;
}

/** Krysalis_Backend wraps thread; embedded backend returns flat ChatThreadResponse */
export type ThreadAgentAssignApiResponse =
  | ChatThreadResponse
  | {
      thread: ChatThreadResponse;
      active_agent?: KrybotAgentSummary;
    };

export function normalizeThreadAgentAssignResponse(
  data: ThreadAgentAssignApiResponse
): ChatThreadResponse {
  return 'thread' in data ? data.thread : data;
}

export interface ChatMessageResponse {
  id: string;
  thread_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  agent_run_id?: string;
  prompt_template_key?: string;
  /** Krybot turn response / merged fields */
  llm_metadata?: Record<string, unknown>;
  /** `GET .../history` includes full row metadata (artifact_payload, command_suggestions, …) */
  metadata?: Record<string, unknown>;
  timestamp: string;
}

/** Run orchestration session (DAG / invokes); backend `krybot_orchestration` */
export type KrybotOrchestrationStatus = 'active' | 'completed' | 'failed' | 'cancelled';

export interface KrybotOrchestrationRecord {
  id: string;
  thread_id?: string | null;
  user_id?: string | null;
  root_run_id?: string | null;
  status: KrybotOrchestrationStatus;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
  updated_at?: string;
}

export interface KrybotOrchestrationEdge {
  id: string;
  orchestration_id: string;
  from_run_id: string;
  to_run_id: string;
  target_agent_id?: string | null;
  invoke_key?: string | null;
  sequence_order: number;
  metadata?: Record<string, unknown> | null;
  created_at?: string;
}

export interface KrybotOrchestrationGraph {
  orchestration: KrybotOrchestrationRecord;
  edges: KrybotOrchestrationEdge[];
}

export interface KrybotOrchestrationCreate {
  thread_id?: string;
  user_id?: string;
  root_run_id?: string;
  metadata?: Record<string, unknown>;
}

export interface KrybotInvokeEdgeCreate {
  from_run_id: string;
  to_run_id: string;
  target_agent_id?: string;
  invoke_key?: string;
  sequence_order?: number;
  metadata?: Record<string, unknown>;
}

export interface KrybotSpawnChildRunPayload {
  parent_run_id: string;
  target_agent_id: string;
  input_payload?: Record<string, unknown>;
  invoke_key?: string;
  sequence_order?: number;
  inherit_artifact_scope?: boolean;
}

export interface KrybotSpawnChildRunResponse {
  child_run_id: string;
  orchestration_id: string;
  parent_run_id: string;
}

